import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { toast } from 'material-react-toastify'
import { Modal as LumenModal, Button, Icons } from '@eqworks/lumen-labs'


const useStyles = makeStyles(({
  modalContent: {
    position: 'relative',
    height: '100%',
    minHeight: 0,

    '& .modal-content-container': {
      height: '100%',
      padding: '1.25rem 0.938rem',
      display: 'flex',
      flexDirection: 'column',
    },

    '& .step-container': {
      width: '100%',
      marginBottom: '0.938rem',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderWidth: '1px',
      borderRadius: '4px',
      borderStyle: 'solid',

      '& .label': {
        width: '16.25rem',
        padding: '0.5rem 0',
        display: 'flex',
        justifyContent: 'center',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        letterSpacing: '0.016rem',
      },

      '& .active': {
        borderRadius: '4px',
        boxShadow: '0px 2px 8px rgba(54, 111, 228, 0.1)',
      },
    },

    '& .loading-container': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 10,
      opacity: 0.7,
    },
  },

  footer: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  footerFirst: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}))


const Modal = ({ steps, getPage, close, handleNext, validate, handleBack, load, isSteps, setToastResponse }) => {
  const classes = useStyles()
  const [loaded, setLoaded] = useState(false)
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    if (typeof load === 'function') {
      load().then(() => setLoaded(true))
    }
  }, [load])

  const next = async (e) => {
    if (activeStep === steps.length - 1) {
      try {
        setLoaded(false)
        await handleNext(activeStep, e)
        toast.success('Success')
        close()
      } catch (e) {
        setLoaded(true)
        console.error(e)
        toast.error(`Error: ${e.message}`)
      }
      return
    }
  
    if (typeof validate === 'function' && !validate(activeStep)) {
      toast.warning('Please fill in all required fields.')
      return
    }

    setLoaded(false)
    const proceed = () => setActiveStep(activeStep => activeStep + 1)
    if (typeof handleNext === 'function') {
      try {
        await handleNext(activeStep, e)
        proceed()
      } catch (e) {
        console.error(e)
        setToastResponse({
          res: 'Error',
          status: `${e.response?.status || ''}`,
          message: `${e.response?.data?.message || e.message}`,
        })
      }
    } else {
      proceed()
    }
    setLoaded(true)
  }

  const back = async (e) => {
    setLoaded(false)
    const proceed = () => setActiveStep(activeStep => activeStep - 1)
    if (typeof handleBack === 'function') {
      try {
        await handleBack(activeStep, e)
        proceed()
      } catch (e) {
        console.error(e)
      }
    } else {
      proceed()
    }
    setLoaded(true)
  }

  const getLoading = () => (
    <div className={'loading-container bg-secondary-200'}>
      <Icons.CircleLoading className='animate-spin fill-current text-primary-600' size='lg' />
    </div>
  )

  return (
    <>
      <div className={classes.modalContent}>
        {!loaded && getLoading()}
        <LumenModal.Content className='modal-content-container'>
          {isSteps &&
              <div className={'step-container bg-secondary-100 border-secondary-100 text-secondary-400'}>
                {steps.map((label, index) => (
                  <div 
                    key={index} 
                    className={`label ${activeStep === index && 'active bg-secondary-50 text-interactive-500'} ${activeStep > index && 'text-interactive-500'}`}>
                    {index+1}. {label}
                  </div>
                ))}
              </div>
          }
          {getPage(activeStep)}
        </LumenModal.Content>
      </div>
      { isSteps && 
        <LumenModal.Footer>
          <div className={`footer-container ${activeStep > 0 ? `${classes.footer}` : `${classes.footerFirst}`}`}>
            {activeStep > 0 && 
              <Button 
                variant='borderless' 
                size='lg' 
                onClick={back}
                startIcon={<Icons.ArrowLeft size='lg' />}
                disabled={activeStep === 0} 
              >
                {activeStep > 0 ? `Previous: ${steps[activeStep-1]}` : 'Previous'}
              </Button>
            }
            <Button 
              variant='filled' 
              size='lg'
              onClick={next} 
              endIcon={<Icons.ArrowRight size='lg' />}
              disabled={!(validate(activeStep) && loaded)}
            >
              {activeStep === steps.length - 1 ? 'Submit' : `Next: ${steps[activeStep+1]}`}
            </Button>
          </div>
        </LumenModal.Footer>
      }
    </>
  )
}

Modal.propTypes = {
  /**
   * The steps label for the Stepper.
   */
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * Gets the different pages given the page number (0-indexed).
   * @param {number} page current page number
   * 
   * @returns {Element} the page to display
   */
  getPage: PropTypes.func.isRequired,
  /**
   * A callable function that closes the the modal.
   */
  close: PropTypes.func.isRequired,
  /**
   * The modal will stop loading once the Promise resolves.
   * If this doesn't exist, it'll proceed right away.
   * 
   * @param {number} activeStep current step before changes (0 indexed)
   * @param {Event} e onClick event
   * 
   * @returns {Promise<void>}
   */
  handleBack: PropTypes.func,
  /**
   * The modal will stop loading once the Promise resolves. 
   * If this doesn't exist, it'll proceed right away.
   * @param {number} activeStep current step before changes (0 indexed)
   * @param {Event} e onClick event
   * 
   * @returns {Promise<void>}
   */
  handleNext: PropTypes.func,
  /**
   * Local validations done to a page before calling API endpoints.
   * @param {number} activeStep current step that needs to be validated (0 indexed)
   * 
   * @returns {boolean} whether or not it is good to proceed to `handleNext`
   */
  validate: PropTypes.func,
  /**
   * If there is any information that needs to be loaded for the first page.
   * 
   * @returns {Promise<void>}
   */
  load: PropTypes.func,
  isSteps: PropTypes.bool,
  setToastResponse: PropTypes.func,
}

Modal.defaultProps = {
  handleBack: async () => {},
  handleNext: async () => {},
  load: async () => {},
  validate: () => true,
  isSteps: true,
}

export default Modal
